<template>
  <main
    v-if="content && copy"
    class="text-white">
    <div class="bg-gray-500 pb-12 lg:pb-24">
      <JobHeader />

      <Container class="flex flex-wrap items-start justify-between space-x-6 lg:flex-nowrap">
        <div class="">
          <h1 class="max-w-2xl whitespace-pre-line text-5xl tracking-tight text-white lg:text-7xl lg:leading-tight">
            {{ content.title }}
          </h1>

          <p
            class="mt-6 max-w-2xl whitespace-pre-line font-normal leading-snug text-gray-100 lg:text-xl lg:leading-snug">
            {{ content.description }}
          </p>

          <JobSidebar
            :division="content.division"
            :employment-type="content.employmentType"
            :work-types="content.workTypes"
            :content="{
              locations: { title: copy.sidebar.locations, items: content.locations },
              recruiter: { title: copy.sidebar.recruiter, id: content.recruiter },
              employers: { title: copy.sidebar.employer, items: content.employers },
              deadline: { title: copy.sidebar.deadline, copy: content.deadline }
            }"
            :cta="copy.sidebar.cta"
            class="mt-12 block flex-shrink-0 lg:hidden"
            @button-click="handleClick" />

          <JobContent
            :content="{
              responsibilities: {
                title: copy.main.responsibilities,
                titleChinese: copy.main.responsibilitiesChinese,
                copy: content.responsibilities
              },
              skills: {
                title: copy.main.skills,
                titleChinese: copy.main.skillsChinese,
                copy: content.skills
              },
              experience: {
                title: copy.main.experience,
                titleChinese: copy.main.experienceChinese,
                copy: content.experience
              },
              offer: {
                title: copy.main.offer,
                titleChinese: copy.main.offerChinese,
                copy: content.offer
              },
              compensation: {
                title: copy.main.compensation,
                titleChinese: copy.main.compensationChinese,
                copy: content.compensation
              }
            }"
            :si="content.employers.includes(Employer.EKIPA2) ? { title: copy.main.si, content: content.si } : null"
            :privacy-type="content.privacyType"
            class="" />
        </div>

        <JobSidebar
          :division="content.division"
          :employment-type="content.employmentType"
          :work-types="content.workTypes"
          :content="{
            locations: { title: copy.sidebar.locations, items: content.locations },
            recruiter: { title: copy.sidebar.recruiter, id: content.recruiter },
            employers: { title: copy.sidebar.employer, items: content.employers },
            deadline: { title: copy.sidebar.deadline, copy: content.deadline }
          }"
          :cta="copy.sidebar.cta"
          class="top-24 hidden flex-shrink-0 lg:sticky lg:block"
          @button-click="handleClick" />
      </Container>
    </div>

    <JobModalPublic
      :id="entryId"
      :is-open="isModalOpen"
      @toggle="setIsOpen" />

    <Container class="lg:pt-12">
      <div class="relative flex min-h-[530px] items-center justify-center overflow-hidden px-4 py-12 lg:mt-12">
        <NuxtImg
          :src="copy.image.image"
          :alt="copy.image.title || ''"
          class="absolute inset-0 h-full w-full rounded-3xl object-cover"
          sizes="xs:87vw sm:87vw md:87vw lg:87vw xl:1140px xxl:1140px"
          loading="lazy" />

        <div
          class="absolute inset-0 h-full w-full rounded-3xl object-cover"
          style="background: linear-gradient(360deg, rgba(10, 10, 10, 0.7) 0%, rgba(10, 10, 10, 0) 100%)" />

        <h3 class="relative text-center text-5xl text-white lg:text-6xl">
          {{ copy.image.title }}
        </h3>
      </div>
      <div class="grid min-h-[463px] gap-6 pt-24 md:grid-cols-3">
        <CardGradient
          v-for="(card, index) in copy.cards"
          :key="index"
          :wide="false"
          class="min-h-[463px]"
          v-bind="card" />
      </div>
    </Container>

    <SectionHeading
      :title="copy.glassdoor.title"
      type="primary"
      :cta="copy.glassdoor.cta" />
  </main>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue'
  import { useRoute, useRouter } from 'vue-router'

  import { Employer } from '~/constants/jobs'
  import routesList from '~/content/routes.json'
  import { getFirstQueryValue } from '~/utils/query'
  import { stripEndingSlash } from '~/utils/urls'

  const { $event, $lenisMount } = useNuxtApp()
  const route = useRoute()
  const router = useRouter()
  const generalData = await useAsyncData('jobs-general', () => queryContent('globals', 'jobs').findOne())
  const isModalOpen = ref(false)

  const uri = stripEndingSlash(route.path)

  const page = routesList.find(item => item.url === uri)

  let entryId = ''

  if (!page) {
    throw createError({
      statusCode: 404,
      statusMessage: 'Page Not Found',
      fatal: true
    })
  } else {
    entryId = page.id
  }

  const { data } = await useAsyncData(entryId, () => queryContent('jobs').where({ id: entryId }).findOne())

  const content = computed(() => {
    if (data && data.value) {
      return data.value
    }

    return false
  })

  const copy = computed(() => {
    if (generalData.data && generalData.data.value) {
      return generalData.data.value
    }

    return false
  })

  useHead({
    title: data.value?.meta?.title,
    link: [
      {
        rel: 'canonical',
        href: `https://outfit7.com${route.path}`
      }
    ],
    meta: [
      { hid: 'description', name: 'description', content: data.value?.meta?.description },

      { hid: 'og:title', property: 'og:title', content: data.value?.social?.title },
      // { hid: 'og:url', property: 'og:url', content: this.pageUrl },
      { hid: 'og:description', property: 'og:description', content: data.value?.social?.description },
      { hid: 'og:image', property: 'og:image', content: data.value?.social?.image },

      { hid: 'twitter:title', name: 'twitter:title', content: data.value?.social?.title },
      // { hid: 'twitter:url', name: 'twitter:url', content: this.pageUrl },
      { hid: 'twitter:description', name: 'twitter:description', content: data.value?.meta?.description },
      { hid: 'twitter:image', name: 'twitter:image', content: data.value?.social?.image }
    ]
  })

  onMounted(() => {
    $lenisMount()
  })

  function setIsOpen(state: boolean) {
    isModalOpen.value = state
    syncGreenhouseJobQueryParam(state).catch(console.error)
    if (!content.value || !content.value.title) {
      return
    }
    if (isModalOpen.value) {
      $event('Job Application', { props: { Open: content.value.title } })
    } else {
      $event('Job Application', { props: { Close: content.value.title } })
    }
  }

  function handleClick() {
    setIsOpen(true)
  }

  async function syncGreenhouseJobQueryParam(isOpen: boolean) {
    const currentGreenhouseJobId = getFirstQueryValue(route.query.gh_jid)

    if ((isOpen && currentGreenhouseJobId === entryId) || (!isOpen && !currentGreenhouseJobId)) {
      return
    }

    const query = { ...route.query }

    if (isOpen) {
      query.gh_jid = entryId
    } else {
      delete query.gh_jid
    }

    await router.replace({
      path: route.path,
      query,
      hash: route.hash
    })
  }
</script>
